import { ERROR_CODES } from "./codes";
import { ERROR_TYPES } from "./errorTypes";

import type { ErrorCode } from "./codes";
import type { ErrorType } from "./errorTypes";

export interface ErrorConfigEntry {
  message: string;
  type: ErrorType;
  factory_code: ErrorCode;
  cause?: Record<string, any> | unknown;
}

type ErrorConfigType = {
  [_ in ErrorCode]: ErrorConfigEntry;
};

export const ERROR_CONFIG: ErrorConfigType = {
  [ERROR_CODES.INVALID_QUERY_PARAMETERS_ERROR]: {
    message: "Invalid query parameters",
    factory_code: ERROR_CODES.INVALID_QUERY_PARAMETERS_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.INVALID_BODY_PARAMETERS_ERROR]: {
    message: "Invalid body parameters",
    factory_code: ERROR_CODES.INVALID_BODY_PARAMETERS_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.UPLOADING_MEDIA_TO_IMAGEKIT_ERROR]: {
    message: "Error uploading media to ImageKit",
    factory_code: ERROR_CODES.UPLOADING_MEDIA_TO_IMAGEKIT_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.VAPI_RECORDING_AUTH_ERROR]: {
    message: "VAPI rejected the authenticated recording request",
    factory_code: ERROR_CODES.VAPI_RECORDING_AUTH_ERROR,
    type: ERROR_TYPES.UNAUTHORIZED_ERROR,
  },
  [ERROR_CODES.VAPI_RECORDING_NOT_FOUND]: {
    message: "VAPI has no recording for this call",
    factory_code: ERROR_CODES.VAPI_RECORDING_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.VAPI_RECORDING_REDIRECT_ERROR]: {
    message: "VAPI recording request did not return a signed download redirect",
    factory_code: ERROR_CODES.VAPI_RECORDING_REDIRECT_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.VAPI_RECORDING_DOWNLOAD_ERROR]: {
    message: "Failed to download the VAPI recording",
    factory_code: ERROR_CODES.VAPI_RECORDING_DOWNLOAD_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SENDING_TEXT_MESSAGE_ERROR]: {
    message: "Error sending text message",
    factory_code: ERROR_CODES.SENDING_TEXT_MESSAGE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GEORESTRICTED_PHONE_NUMBER_ERROR]: {
    message: "Phone number is in a geo-restricted region",
    factory_code: ERROR_CODES.GEORESTRICTED_PHONE_NUMBER_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.OPTED_OUT_PHONE_NUMBER_ERROR]: {
    message: "Recipient has opted out of receiving text messages",
    factory_code: ERROR_CODES.OPTED_OUT_PHONE_NUMBER_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.EXTRACTING_INFO_FROM_MEDIA_URL_ERROR]: {
    message: "Error extracting info from media url",
    factory_code: ERROR_CODES.EXTRACTING_INFO_FROM_MEDIA_URL_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SITE_VERIFICATION_GET_VERIFICATION_STATUS_ERROR]: {
    message: "Error getting verification status",
    factory_code: ERROR_CODES.SITE_VERIFICATION_GET_VERIFICATION_STATUS_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.PRISMA_UPDATE_WEBSITE_ERROR]: {
    message: "Error updating website",
    factory_code: ERROR_CODES.PRISMA_UPDATE_WEBSITE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SEND_EMAIL_ERROR]: {
    message: "Error sending email",
    factory_code: ERROR_CODES.SEND_EMAIL_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_WEBHOOK_PROCESSING_ERROR]: {
    message: "Error processing Clerk webhook",
    factory_code: ERROR_CODES.CLERK_WEBHOOK_PROCESSING_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.EMPTY_CLERK_CLIENT_DATA_ERROR]: {
    message: "Empty Clerk client data",
    factory_code: ERROR_CODES.EMPTY_CLERK_CLIENT_DATA_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_GET_CLIENT_ERROR]: {
    message: "Error getting client from Clerk",
    factory_code: ERROR_CODES.CLERK_GET_CLIENT_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_ORGANIZATION_NOT_FOUND_ERROR]: {
    message: "Organization not found in Clerk",
    factory_code: ERROR_CODES.CLERK_ORGANIZATION_NOT_FOUND_ERROR,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.CLERK_GET_ORGANIZATION_ERROR]: {
    message: "Error getting organization from Clerk",
    factory_code: ERROR_CODES.CLERK_GET_ORGANIZATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_ADD_DOMAIN_ERROR]: {
    message: "Error adding domain",
    factory_code: ERROR_CODES.GSC_ADD_DOMAIN_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.UPDATE_INTEGRATION_METADATA_ERROR]: {
    message: "Error updating integration metadata",
    factory_code: ERROR_CODES.UPDATE_INTEGRATION_METADATA_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_SUBMIT_SITEMAP_ERROR]: {
    message: "Error submitting sitemap",
    factory_code: ERROR_CODES.GSC_SUBMIT_SITEMAP_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SITE_VERIFICATION_INSERT_TOKEN_ERROR]: {
    message: "Error inserting verification token",
    factory_code: ERROR_CODES.SITE_VERIFICATION_INSERT_TOKEN_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SITE_VERIFICATION_TOKEN_NOT_FOUND_ERROR]: {
    message: "Verification token not found",
    factory_code: ERROR_CODES.SITE_VERIFICATION_TOKEN_NOT_FOUND_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.POLL_GSC_INTEGRATION_INVALID_REQUEST]: {
    message: "Invalid polling request",
    factory_code: ERROR_CODES.POLL_GSC_INTEGRATION_INVALID_REQUEST,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.INDEX_NOW_SUBMISSION_ERROR]: {
    message: "Error submitting to IndexNow",
    factory_code: ERROR_CODES.INDEX_NOW_SUBMISSION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_GET_SITEMAPS_ERROR]: {
    message: "Error fetching sitemaps",
    factory_code: ERROR_CODES.GSC_GET_SITEMAPS_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_DOMAIN_MISCONFIGURED]: {
    message: "Custom domain is misconfigured or does not exist",
    factory_code: ERROR_CODES.GSC_DOMAIN_MISCONFIGURED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.SITE_VERIFICATION_NOT_OWNER_ERROR]: {
    message: "You are not an owner of this site.",
    factory_code: ERROR_CODES.SITE_VERIFICATION_NOT_OWNER_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.PRISMA_UPDATE_INTEGRATION_ERROR]: {
    message: "Error updating integration",
    factory_code: ERROR_CODES.PRISMA_UPDATE_INTEGRATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_INTEGRATION_NOT_FOUND]: {
    message: "GSC integration not found",
    factory_code: ERROR_CODES.GSC_INTEGRATION_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.GSC_WEBSITE_NOT_FOUND]: {
    message: "Website not found",
    factory_code: ERROR_CODES.GSC_WEBSITE_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.PRISMA_GET_WEBSITE_ERROR]: {
    message: "Error fetching website",
    factory_code: ERROR_CODES.PRISMA_GET_WEBSITE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_INTEGRATION_NOT_FOUND]: {
    message: "Google Business Profile integration not found",
    factory_code: ERROR_CODES.GBP_INTEGRATION_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.RELATED_CATEGORIES]: {
    message: "Error fetching related categories",
    factory_code: ERROR_CODES.RELATED_CATEGORIES,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SERVICES_FOR_CATEGORY]: {
    message: "Error fetching services for category",
    factory_code: ERROR_CODES.SERVICES_FOR_CATEGORY,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.PRISMA_GET_INTEGRATION]: {
    message: "Error fetching integration",
    factory_code: ERROR_CODES.PRISMA_GET_INTEGRATION,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GET_AUTH_AND_WEBSITE]: {
    message: "Error fetching authentication and website",
    factory_code: ERROR_CODES.GET_AUTH_AND_WEBSITE,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.PRISMA_UPDATE]: {
    message: "Error updating data in database",
    factory_code: ERROR_CODES.PRISMA_UPDATE,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.NOT_FOUND]: {
    message: "The requested resource was not found",
    factory_code: ERROR_CODES.NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.OPENAI_API_ERROR]: {
    message: "OpenAI API error occurred during AI analysis",
    factory_code: ERROR_CODES.OPENAI_API_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.QUOTE_DETAILS_INSUFFICIENT]: {
    message: "Not enough job details to create a quote",
    factory_code: ERROR_CODES.QUOTE_DETAILS_INSUFFICIENT,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.PRISMA_FIND]: {
    message: "Error fetching data",
    factory_code: ERROR_CODES.PRISMA_FIND,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.DEFAULT_GROUP_DELETE]: {
    message: "Default group must have at least one service",
    factory_code: ERROR_CODES.DEFAULT_GROUP_DELETE,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.PARSE_GROUP_INFO]: {
    message: "Error parsing group info",
    factory_code: ERROR_CODES.PARSE_GROUP_INFO,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.ORGANIZATION_NOT_FOUND]: {
    message: "Organization not found",
    factory_code: ERROR_CODES.ORGANIZATION_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.UNAUTHORIZED]: {
    message: "Unauthorized",
    factory_code: ERROR_CODES.UNAUTHORIZED,
    type: ERROR_TYPES.UNAUTHORIZED_ERROR,
  },
  [ERROR_CODES.PAGESPEED_ANALYSIS_FAILED]: {
    message: "Error running pagespeed analysis",
    factory_code: ERROR_CODES.PAGESPEED_ANALYSIS_FAILED,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.ANALYTICS_INTEGRATION_NOT_FOUND]: {
    message: "Analytics integration not found",
    factory_code: ERROR_CODES.ANALYTICS_INTEGRATION_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.GBP_INTEGRATION_INITIALIZATION_ERROR]: {
    message: "Error initializing GBP integration",
    factory_code: ERROR_CODES.GBP_INTEGRATION_INITIALIZATION_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.INVALID_PAGESPEED_API_KEY]: {
    message: "Invalid pagespeed api key",
    factory_code: ERROR_CODES.INVALID_PAGESPEED_API_KEY,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_ANALYTICS_INTEGRATION_EXISTS]: {
    message: "Google Search Console analytics integration already exists",
    factory_code: ERROR_CODES.GSC_ANALYTICS_INTEGRATION_EXISTS,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.PAGESPEED_INTEGRATION_INITIALIZATION_ERROR]: {
    message: "Unable to initialize page speed integration",
    factory_code: ERROR_CODES.PAGESPEED_INTEGRATION_INITIALIZATION_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.INVALID_CUSTOM_DOMAIN_ERROR]: {
    message: "No custom domain or misconfigured custom domain",
    factory_code: ERROR_CODES.INVALID_CUSTOM_DOMAIN_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.GSC_METRICS_ERROR]: {
    message: "Error fetching GSC metrics",
    factory_code: ERROR_CODES.GSC_METRICS_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.UMAMI_METRICS_ERROR]: {
    message: "Error fetching Umami metrics",
    factory_code: ERROR_CODES.UMAMI_METRICS_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.UMAMI_INITIALIZATION_ERROR]: {
    message: "Error initializing Umami",
    factory_code: ERROR_CODES.UMAMI_INITIALIZATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_QUERY_FAILED]: {
    message: "Error fetching GSC metrics",
    factory_code: ERROR_CODES.GSC_QUERY_FAILED,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GSC_NO_DATA_FOR_DATE]: {
    message: "No GSC data for date",
    factory_code: ERROR_CODES.GSC_NO_DATA_FOR_DATE,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.INVALID_REQUEST]: {
    message: "Invalid request",
    factory_code: ERROR_CODES.INVALID_REQUEST,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.ANALYTICS_REPORT_INSUFFICIENT_DATA]: {
    message: "Insufficient data to generate report",
    factory_code: ERROR_CODES.ANALYTICS_REPORT_INSUFFICIENT_DATA,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.ANALYTICS_REPORT_NOT_AVAILABLE]: {
    message: "Report not available",
    factory_code: ERROR_CODES.ANALYTICS_REPORT_NOT_AVAILABLE,
    type: ERROR_TYPES.BAD_REQUEST_ERROR,
  },
  [ERROR_CODES.CLERK_GET_ORGANIZATION_MEMBERS_ERROR]: {
    message: "Error fetching organization members",
    factory_code: ERROR_CODES.CLERK_GET_ORGANIZATION_MEMBERS_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_INVITE_MEMBER_ERROR]: {
    message: "Error inviting member",
    factory_code: ERROR_CODES.CLERK_INVITE_MEMBER_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_REMOVE_MEMBER_ERROR]: {
    message: "Error removing member",
    factory_code: ERROR_CODES.CLERK_REMOVE_MEMBER_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_UPDATE_MEMBER_ERROR]: {
    message: "Error updating member",
    factory_code: ERROR_CODES.CLERK_UPDATE_MEMBER_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_USER_NOT_FOUND]: {
    message: "User not found",
    factory_code: ERROR_CODES.CLERK_USER_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.GET_FORMATTED_PROMPT_ERROR]: {
    message: "Error getting formatted prompt",
    factory_code: ERROR_CODES.GET_FORMATTED_PROMPT_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.DEAL_CLOSER_NOT_FOUND]: {
    message: "Deal closer not found",
    factory_code: ERROR_CODES.DEAL_CLOSER_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.FUNNEL_NOT_FOUND]: {
    message: "Funnel not found",
    factory_code: ERROR_CODES.FUNNEL_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.DEAL_NOT_FOUND]: {
    message: "Deal not found",
    factory_code: ERROR_CODES.DEAL_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.DEAL_CLOSER_NAME_EXISTS]: {
    message: "A deal closer with this name already exists",
    factory_code: ERROR_CODES.DEAL_CLOSER_NAME_EXISTS,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.FUNNEL_NAME_EXISTS]: {
    message: "A funnel with this name already exists",
    factory_code: ERROR_CODES.FUNNEL_NAME_EXISTS,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.DEAL_CREATE_ERROR]: {
    message: "Error creating deal",
    factory_code: ERROR_CODES.DEAL_CREATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.DEAL_UPDATE_ERROR]: {
    message: "Error updating deal",
    factory_code: ERROR_CODES.DEAL_UPDATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.DEAL_CLOSER_CREATE_ERROR]: {
    message: "Error creating deal closer",
    factory_code: ERROR_CODES.DEAL_CLOSER_CREATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.DEAL_CLOSER_UPDATE_ERROR]: {
    message: "Error updating deal closer",
    factory_code: ERROR_CODES.DEAL_CLOSER_UPDATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.DEAL_CLOSER_DELETE_ERROR]: {
    message: "Error deleting deal closer",
    factory_code: ERROR_CODES.DEAL_CLOSER_DELETE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.FUNNEL_CREATE_ERROR]: {
    message: "Error creating funnel",
    factory_code: ERROR_CODES.FUNNEL_CREATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.FUNNEL_UPDATE_ERROR]: {
    message: "Error updating funnel",
    factory_code: ERROR_CODES.FUNNEL_UPDATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.FUNNEL_DELETE_ERROR]: {
    message: "Error deleting funnel",
    factory_code: ERROR_CODES.FUNNEL_DELETE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.STRIPE_SUBSCRIPTION_RETRIEVAL_ERROR]: {
    message: "Error retrieving subscription from Stripe",
    factory_code: ERROR_CODES.STRIPE_SUBSCRIPTION_RETRIEVAL_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SUBSCRIPTION_PRICE_NOT_FOUND]: {
    message: "No price found for subscription",
    factory_code: ERROR_CODES.SUBSCRIPTION_PRICE_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.SUBSCRIPTION_PRICE_AMOUNT_NOT_FOUND]: {
    message: "No price amount found for subscription",
    factory_code: ERROR_CODES.SUBSCRIPTION_PRICE_AMOUNT_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.PAYMENT_LINK_NOT_FOUND]: {
    message: "No matching payment link found for subscription amount",
    factory_code: ERROR_CODES.PAYMENT_LINK_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.PUPPETEER_LAUNCH_ERROR]: {
    message: "Error launching puppeteer",
    factory_code: ERROR_CODES.PUPPETEER_LAUNCH_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.BLOB_DELETE_ERROR]: {
    message: "Error deleting blob from storage",
    factory_code: ERROR_CODES.BLOB_DELETE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.ANALYTICS_REPORT_DELETE_ERROR]: {
    message: "Error deleting analytics report",
    factory_code: ERROR_CODES.ANALYTICS_REPORT_DELETE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_SCORE_CALCULATION_ERROR]: {
    message: "Error calculating GBP score",
    factory_code: ERROR_CODES.GBP_SCORE_CALCULATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_INTEGRATION_CREATE_ERROR]: {
    message: "Error creating GBP integration",
    factory_code: ERROR_CODES.GBP_INTEGRATION_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_SCORE_BUSINESS_INFO_FETCH_ERROR]: {
    message: "Error fetching business information for GBP score calculation",
    factory_code: ERROR_CODES.GBP_SCORE_BUSINESS_INFO_FETCH_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_SCORE_REVIEWS_FETCH_ERROR]: {
    message: "Error fetching reviews for GBP score calculation",
    factory_code: ERROR_CODES.GBP_SCORE_REVIEWS_FETCH_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_SCORE_LOCATION_MEDIA_FETCH_ERROR]: {
    message: "Error fetching location media for GBP score calculation",
    factory_code: ERROR_CODES.GBP_SCORE_LOCATION_MEDIA_FETCH_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_SCORE_DESCRIPTION_EVALUATION_ERROR]: {
    message: "Error evaluating business description for GBP score calculation",
    factory_code: ERROR_CODES.GBP_SCORE_DESCRIPTION_EVALUATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_SCORE_UPDATE_FIELDS_ERROR]: {
    message: "Error updating GBP fields",
    factory_code: ERROR_CODES.GBP_SCORE_UPDATE_FIELDS_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.KNOCK_WORKFLOW_TRIGGER_ERROR]: {
    message: "Error sending email",
    factory_code: ERROR_CODES.KNOCK_WORKFLOW_TRIGGER_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SECTION_MOVE_TO_POSITION_ERROR]: {
    message: "Error moving section",
    factory_code: ERROR_CODES.SECTION_MOVE_TO_POSITION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SECTION_NOT_FOUND]: {
    message: "Section not found",
    factory_code: ERROR_CODES.SECTION_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.SECTION_INVALID_MOVE]: {
    message: "Invalid section move",
    factory_code: ERROR_CODES.SECTION_INVALID_MOVE,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.CLERK_GET_ORGANIZATION_INVITATIONS_ERROR]: {
    message: "Error fetching organization invitations",
    factory_code: ERROR_CODES.CLERK_GET_ORGANIZATION_INVITATIONS_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CLERK_REVOKE_ORGANIZATION_INVITATION_ERROR]: {
    message: "Error revoking organization invitation",
    factory_code: ERROR_CODES.CLERK_REVOKE_ORGANIZATION_INVITATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.NO_MEDIA_PROVIDED_FOR_INSTAGRAM_POST]: {
    message: "No media provided for Instagram post",
    factory_code: ERROR_CODES.NO_MEDIA_PROVIDED_FOR_INSTAGRAM_POST,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.TRANSCRIPT_ANALYSIS_INVALID_REQUEST]: {
    message: "Invalid transcript analysis request",
    factory_code: ERROR_CODES.TRANSCRIPT_ANALYSIS_INVALID_REQUEST,
    type: ERROR_TYPES.BAD_REQUEST_ERROR,
  },
  [ERROR_CODES.TRANSCRIPT_ANALYSIS_OPENAI_ERROR]: {
    message: "Error analyzing transcript with OpenAI",
    factory_code: ERROR_CODES.TRANSCRIPT_ANALYSIS_OPENAI_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TRANSCRIPT_ANALYSIS_PARSE_ERROR]: {
    message: "Error parsing OpenAI response for transcript analysis",
    factory_code: ERROR_CODES.TRANSCRIPT_ANALYSIS_PARSE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SOCIAL_AUTOMATION_NOT_FOUND]: {
    message: "No social automation found",
    factory_code: ERROR_CODES.SOCIAL_AUTOMATION_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.SOCIAL_AUTOMATION_PAUSED_DUE_TO_INACTIVITY]: {
    message: "Social automation is paused due to inactivity",
    factory_code: ERROR_CODES.SOCIAL_AUTOMATION_PAUSED_DUE_TO_INACTIVITY,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.SOCIAL_AUTOMATION_NOT_ACTIVE]: {
    message: "Social automation is not active",
    factory_code: ERROR_CODES.SOCIAL_AUTOMATION_NOT_ACTIVE,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.SOCIAL_AUTOMATION_NO_PRIMARY_PHONE]: {
    message: "Social automation has no primary phone number",
    factory_code: ERROR_CODES.SOCIAL_AUTOMATION_NO_PRIMARY_PHONE,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.SOCIAL_POST_FETCH_ERROR]: {
    message: "Error fetching social posts",
    factory_code: ERROR_CODES.SOCIAL_POST_FETCH_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SVG_SANITIZATION_FAILED]: {
    message:
      "SVG file contains dangerous content that cannot be safely removed. Please use a different SVG file or remove any scripts/dangerous elements manually.",
    factory_code: ERROR_CODES.SVG_SANITIZATION_FAILED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.SERPAPI_CONFIGURATION_ERROR]: {
    message: "serpapi configuration error",
    factory_code: ERROR_CODES.SERPAPI_CONFIGURATION_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.SERPAPI_VALIDATION_ERROR]: {
    message: "serpapi input validation error",
    factory_code: ERROR_CODES.SERPAPI_VALIDATION_ERROR,
    type: ERROR_TYPES.BAD_REQUEST_ERROR,
  },
  [ERROR_CODES.SERPAPI_REQUEST_ERROR]: {
    message: "serpapi request failed",
    factory_code: ERROR_CODES.SERPAPI_REQUEST_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.SERPAPI_INVALID_PLACE_ID]: {
    message: "The provided Google Place ID is invalid",
    factory_code: ERROR_CODES.SERPAPI_INVALID_PLACE_ID,
    type: ERROR_TYPES.BAD_REQUEST_ERROR,
  },
  [ERROR_CODES.SERPAPI_GOOGLE_MAPS_NO_RESULTS]: {
    message: "Google Maps returned no results for this search query",
    factory_code: ERROR_CODES.SERPAPI_GOOGLE_MAPS_NO_RESULTS,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.INVALID_COMPANY_NAME]: {
    message: "Invalid company name",
    factory_code: ERROR_CODES.INVALID_COMPANY_NAME,
    type: ERROR_TYPES.BAD_REQUEST_ERROR,
  },
  [ERROR_CODES.INVALID_PLACE_ID]: {
    message: "The provided Google Place ID is invalid or does not support reviews",
    factory_code: ERROR_CODES.INVALID_PLACE_ID,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.PROCESS_SECTION_ERROR]: {
    message: "Error processing section",
    factory_code: ERROR_CODES.PROCESS_SECTION_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.PROMPT_NOT_DEFINED]: {
    message: "Prompt not defined",
    factory_code: ERROR_CODES.PROMPT_NOT_DEFINED,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.AI_CONTENT_QUALITY_CHECK_FAILED]: {
    message: "AI-generated content failed quality validation",
    factory_code: ERROR_CODES.AI_CONTENT_QUALITY_CHECK_FAILED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.AI_CONTENT_VALIDATION_SERVICE_ERROR]: {
    message: "AI content validation service unavailable",
    factory_code: ERROR_CODES.AI_CONTENT_VALIDATION_SERVICE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.FEATURE_UNAVAILABLE_FOR_SUBSCRIPTION_ERROR]: {
    message: "Feature unavailable for subscription tier, please upgrade to a premium tier",
    factory_code: ERROR_CODES.FEATURE_UNAVAILABLE_FOR_SUBSCRIPTION_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },

  // call tracking related error codes
  [ERROR_CODES.CALL_TRACKING_NUMBER_NOT_FOUND]: {
    message: "Call tracking number not found",
    factory_code: ERROR_CODES.CALL_TRACKING_NUMBER_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.CALL_TRACKING_NUMBER_CREATE_ERROR]: {
    message: "Error creating call tracking number",
    factory_code: ERROR_CODES.CALL_TRACKING_NUMBER_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CALL_TRACKING_NUMBER_UPDATE_ERROR]: {
    message: "Error updating call tracking number",
    factory_code: ERROR_CODES.CALL_TRACKING_NUMBER_UPDATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CALL_TRACKING_NUMBER_DELETE_ERROR]: {
    message: "Error deleting call tracking number",
    factory_code: ERROR_CODES.CALL_TRACKING_NUMBER_DELETE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CALL_RECORD_NOT_FOUND]: {
    message: "Call record not found",
    factory_code: ERROR_CODES.CALL_RECORD_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.CALL_RECORD_CREATE_ERROR]: {
    message: "Error creating call record",
    factory_code: ERROR_CODES.CALL_RECORD_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CALL_RECORD_UPDATE_ERROR]: {
    message: "Error updating call record",
    factory_code: ERROR_CODES.CALL_RECORD_UPDATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_API_ERROR]: {
    message: "Telnyx API error",
    factory_code: ERROR_CODES.TELNYX_API_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_AUTH_ERROR]: {
    message: "Telnyx authentication error",
    factory_code: ERROR_CODES.TELNYX_AUTH_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_NUMBER_SEARCH_ERROR]: {
    message: "Error searching for available numbers with Telnyx",
    factory_code: ERROR_CODES.TELNYX_NUMBER_SEARCH_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_NUMBER_PROVISION_ERROR]: {
    message: "Error provisioning phone number with Telnyx",
    factory_code: ERROR_CODES.TELNYX_NUMBER_PROVISION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_NUMBER_DELETE_ERROR]: {
    message: "Error releasing phone number from Telnyx",
    factory_code: ERROR_CODES.TELNYX_NUMBER_DELETE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_WEBHOOK_SIGNATURE_ERROR]: {
    message: "Invalid Telnyx webhook signature",
    factory_code: ERROR_CODES.TELNYX_WEBHOOK_SIGNATURE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_WEBHOOK_PROCESSING_ERROR]: {
    message: "Error processing Telnyx webhook",
    factory_code: ERROR_CODES.TELNYX_WEBHOOK_PROCESSING_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CALL_FORWARDING_CONFIG_ERROR]: {
    message: "Error configuring call forwarding",
    factory_code: ERROR_CODES.CALL_FORWARDING_CONFIG_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.INVALID_PHONE_NUMBER_ERROR]: {
    message: "Invalid phone number format",
    factory_code: ERROR_CODES.INVALID_PHONE_NUMBER_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.ORGANIC_TRACKING_NUMBER_LIMIT_REACHED]: {
    message: "You can only have one organic tracking number per website",
    factory_code: ERROR_CODES.ORGANIC_TRACKING_NUMBER_LIMIT_REACHED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.TRACKING_NUMBER_DESTINATION_ALREADY_EXISTS]: {
    message: "A tracking number already exists for this destination",
    factory_code: ERROR_CODES.TRACKING_NUMBER_DESTINATION_ALREADY_EXISTS,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.TRACKING_NUMBER_TYPE_NOT_ALLOWED]: {
    message: "This tracking number type is not allowed from the frontend",
    factory_code: ERROR_CODES.TRACKING_NUMBER_TYPE_NOT_ALLOWED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.EXTERNAL_SERVICE_ERROR]: {
    message: "External service error",
    factory_code: ERROR_CODES.EXTERNAL_SERVICE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.TELNYX_TRANSCRIPTION_ERROR]: {
    message: "Error retrieving transcription from Telnyx",
    factory_code: ERROR_CODES.TELNYX_TRANSCRIPTION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },

  [ERROR_CODES.CUSTOMER_RECORD_NOT_FOUND]: {
    message: "Customer record not found",
    factory_code: ERROR_CODES.CUSTOMER_RECORD_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.CUSTOMER_RECORD_CREATE_ERROR]: {
    message: "Error creating customer record",
    factory_code: ERROR_CODES.CUSTOMER_RECORD_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CUSTOMER_RECORD_UPDATE_ERROR]: {
    message: "Error updating customer record",
    factory_code: ERROR_CODES.CUSTOMER_RECORD_UPDATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CUSTOMER_RECORD_DELETE_ERROR]: {
    message: "Error deleting customer record",
    factory_code: ERROR_CODES.CUSTOMER_RECORD_DELETE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CUSTOMER_RECORD_LOOKUP_ERROR]: {
    message: "Error looking up customer record",
    factory_code: ERROR_CODES.CUSTOMER_RECORD_LOOKUP_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CUSTOMER_RECORD_FIND_OR_CREATE_ERROR]: {
    message: "Error finding or creating customer record",
    factory_code: ERROR_CODES.CUSTOMER_RECORD_FIND_OR_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.TRANSCRIPT_EXTRACT_FORM_ANSWERS]: {
    message: "Error extracting form answers from transcript",
    factory_code: ERROR_CODES.TRANSCRIPT_EXTRACT_FORM_ANSWERS,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.FIREFLIES_TRANSCRIPT_GET_ERROR]: {
    message: "Error getting transcript from Fireflies",
    factory_code: ERROR_CODES.FIREFLIES_TRANSCRIPT_GET_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.CONFLICT]: {
    message: "Conflict error",
    factory_code: ERROR_CODES.CONFLICT,
    type: ERROR_TYPES.CONFLICT_ERROR,
  },
  [ERROR_CODES.CREATE_WEBSITE_VERSION_ERROR]: {
    message: "Error creating website version",
    factory_code: ERROR_CODES.CREATE_WEBSITE_VERSION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GET_WEBSITE_VERSION_ERROR]: {
    message: "Error getting website version",
    factory_code: ERROR_CODES.GET_WEBSITE_VERSION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.ROLLBACK_WEBSITE_TO_VERSION_ERROR]: {
    message: "Error rolling back website to version",
    factory_code: ERROR_CODES.ROLLBACK_WEBSITE_TO_VERSION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.WEBSITE_NOT_ACTIVE]: {
    message: "Website is not active",
    factory_code: ERROR_CODES.WEBSITE_NOT_ACTIVE,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.JOB_CREATE_ERROR]: {
    message: "Error creating job",
    factory_code: ERROR_CODES.JOB_CREATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.REVIEW_REQUEST_CREATE_ERROR]: {
    message: "Error creating review request",
    factory_code: ERROR_CODES.REVIEW_REQUEST_CREATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.REVIEW_REQUEST_NOT_FOUND]: {
    message: "Review request not found",
    factory_code: ERROR_CODES.REVIEW_REQUEST_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.REVIEW_REQUEST_UPDATE_ERROR]: {
    message: "Error updating review request status",
    factory_code: ERROR_CODES.REVIEW_REQUEST_UPDATE_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.REVIEW_REQUEST_ALREADY_COMPLETED]: {
    message: "Review request already completed",
    factory_code: ERROR_CODES.REVIEW_REQUEST_ALREADY_COMPLETED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.REVIEW_REQUEST_INVALID_DATA]: {
    message: "Invalid data provided for review request",
    factory_code: ERROR_CODES.REVIEW_REQUEST_INVALID_DATA,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.SEGMENT_SERVER_TRACK_ERROR]: {
    message: "Error tracking event with Segment",
    factory_code: ERROR_CODES.SEGMENT_SERVER_TRACK_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.REVIEW_CREATE_ERROR]: {
    message: "Error creating review",
    factory_code: ERROR_CODES.REVIEW_CREATE_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.REVIEW_UPDATE_ERROR]: {
    message: "Error updating review",
    factory_code: ERROR_CODES.REVIEW_UPDATE_ERROR,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.REVIEW_NOT_FOUND]: {
    message: "This review could not be found on Google.",
    factory_code: ERROR_CODES.REVIEW_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.REVIEW_REPLY_GENERATION_ERROR]: {
    message: "Error generating reply to Google review",
    factory_code: ERROR_CODES.REVIEW_REPLY_GENERATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.REVIEW_IMPORT_INTEGRATION_NOT_FOUND]: {
    message: "Integration not found for historical reviews import",
    factory_code: ERROR_CODES.REVIEW_IMPORT_INTEGRATION_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.REVIEW_IMPORT_NO_OAUTH_TOKENS]: {
    message: "Integration has no OAuth tokens for historical reviews import",
    factory_code: ERROR_CODES.REVIEW_IMPORT_NO_OAUTH_TOKENS,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.REVIEW_IMPORT_ERROR]: {
    message: "Error importing historical Google reviews",
    factory_code: ERROR_CODES.REVIEW_IMPORT_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GOOGLE_REVIEW_REPLY_FAILED]: {
    message: "Failed to reply to Google review",
    factory_code: ERROR_CODES.GOOGLE_REVIEW_REPLY_FAILED,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GOOGLE_TOKEN_REFRESH_FAILED]: {
    message: "Failed to refresh Google token",
    factory_code: ERROR_CODES.GOOGLE_TOKEN_REFRESH_FAILED,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.GBP_PERMISSION_DENIED]: {
    message: "Rebolt's connection to your GBP expired. Please reconnect.",
    factory_code: ERROR_CODES.GBP_PERMISSION_DENIED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.GBP_LOCATION_UNCLAIMED]: {
    message:
      "Google Business Profile location isn't claimed yet, so we can't post the reply. Claim and verify the location in Google Business Profile, then try again.",
    factory_code: ERROR_CODES.GBP_LOCATION_UNCLAIMED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.PARTNERSTACK_CREATE_REFERRAL_LINK_ERROR]: {
    message: "Failed to create referral link",
    factory_code: ERROR_CODES.PARTNERSTACK_CREATE_REFERRAL_LINK_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.PARTNERSTACK_USER_ALREADY_MEMBER]: {
    message: "User is already a member of this company",
    factory_code: ERROR_CODES.PARTNERSTACK_USER_ALREADY_MEMBER,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.POST_IMAGE_DESCRIPTION_ERROR]: {
    message: "Error generating image description",
    factory_code: ERROR_CODES.POST_IMAGE_DESCRIPTION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.POST_TEXT_GENERATION_ERROR]: {
    message: "Error generating social media caption",
    factory_code: ERROR_CODES.POST_TEXT_GENERATION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },

  // rainforest related error codes
  [ERROR_CODES.RAINFOREST_MERCHANT_CREATE_ERROR]: {
    message: "Error creating Rainforest merchant",
    factory_code: ERROR_CODES.RAINFOREST_MERCHANT_CREATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.RAINFOREST_MERCHANT_NOT_FOUND]: {
    message: "Rainforest merchant not found",
    factory_code: ERROR_CODES.RAINFOREST_MERCHANT_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.RAINFOREST_SESSION_CREATE_ERROR]: {
    message: "Error creating Rainforest session",
    factory_code: ERROR_CODES.RAINFOREST_SESSION_CREATE_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.RAINFOREST_API_ERROR]: {
    message: "Rainforest API error",
    factory_code: ERROR_CODES.RAINFOREST_API_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.RAINFOREST_WEBHOOK_PROCESSING_ERROR]: {
    message: "Error processing Rainforest webhook",
    factory_code: ERROR_CODES.RAINFOREST_WEBHOOK_PROCESSING_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.RAINFOREST_PAYIN_CONFIG_ERROR]: {
    message: "Error creating Rainforest payin configuration",
    factory_code: ERROR_CODES.RAINFOREST_PAYIN_CONFIG_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },
  [ERROR_CODES.RAINFOREST_PAYMENT_SESSION_ERROR]: {
    message: "Error creating Rainforest payment session",
    factory_code: ERROR_CODES.RAINFOREST_PAYMENT_SESSION_ERROR,
    type: ERROR_TYPES.EXTERNAL_ERROR,
  },

  // billing/payments related error codes
  [ERROR_CODES.QUOTE_DEPOSIT_NOT_PAID]: {
    message: "Quote deposit has not been paid",
    factory_code: ERROR_CODES.QUOTE_DEPOSIT_NOT_PAID,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.INVOICE_NOT_PAYABLE]: {
    message: "Invoice cannot be paid",
    factory_code: ERROR_CODES.INVOICE_NOT_PAYABLE,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.PAYMENT_AMOUNT_INVALID]: {
    message: "Payment amount is invalid",
    factory_code: ERROR_CODES.PAYMENT_AMOUNT_INVALID,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },

  // client portal related error codes
  [ERROR_CODES.CLIENT_PORTAL_TOKEN_INVALID]: {
    message: "Invalid portal access token",
    factory_code: ERROR_CODES.CLIENT_PORTAL_TOKEN_INVALID,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_TOKEN_EXPIRED]: {
    message: "Portal access token has expired",
    factory_code: ERROR_CODES.CLIENT_PORTAL_TOKEN_EXPIRED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_CUSTOMER_NOT_FOUND]: {
    message: "Customer not found",
    factory_code: ERROR_CODES.CLIENT_PORTAL_CUSTOMER_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_TOKEN_GENERATION_ERROR]: {
    message: "Error generating portal access token",
    factory_code: ERROR_CODES.CLIENT_PORTAL_TOKEN_GENERATION_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_SERVICE_REQUEST_CREATE_ERROR]: {
    message: "Error creating service request",
    factory_code: ERROR_CODES.CLIENT_PORTAL_SERVICE_REQUEST_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_REFERRAL_CREATE_ERROR]: {
    message: "Error creating referral",
    factory_code: ERROR_CODES.CLIENT_PORTAL_REFERRAL_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_REVIEW_REQUEST_ERROR]: {
    message: "Error getting or creating review request",
    factory_code: ERROR_CODES.CLIENT_PORTAL_REVIEW_REQUEST_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_VERIFICATION_REQUIRED]: {
    message: "Verification required to perform this action",
    factory_code: ERROR_CODES.CLIENT_PORTAL_VERIFICATION_REQUIRED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_VERIFICATION_FAILED]: {
    message: "Verification failed",
    factory_code: ERROR_CODES.CLIENT_PORTAL_VERIFICATION_FAILED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_VERIFICATION_LOCKED]: {
    message: "Too many verification attempts",
    factory_code: ERROR_CODES.CLIENT_PORTAL_VERIFICATION_LOCKED,
    type: ERROR_TYPES.EXPECTED_ERROR,
  },
  [ERROR_CODES.CLIENT_PORTAL_VERIFICATION_UNAVAILABLE]: {
    message: "Verification is not available for this account",
    factory_code: ERROR_CODES.CLIENT_PORTAL_VERIFICATION_UNAVAILABLE,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },

  // ai voice assistant related error codes
  [ERROR_CODES.AI_VOICE_ASSISTANT_CREATE_ERROR]: {
    message: "Error creating AI voice assistant",
    factory_code: ERROR_CODES.AI_VOICE_ASSISTANT_CREATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.AI_VOICE_ASSISTANT_UPDATE_ERROR]: {
    message: "Error updating AI voice assistant",
    factory_code: ERROR_CODES.AI_VOICE_ASSISTANT_UPDATE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.AI_VOICE_ASSISTANT_DELETE_ERROR]: {
    message: "Error deleting AI voice assistant",
    factory_code: ERROR_CODES.AI_VOICE_ASSISTANT_DELETE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
  [ERROR_CODES.AI_VOICE_ASSISTANT_NOT_FOUND]: {
    message: "AI voice assistant not found",
    factory_code: ERROR_CODES.AI_VOICE_ASSISTANT_NOT_FOUND,
    type: ERROR_TYPES.NOT_FOUND_ERROR,
  },
  [ERROR_CODES.DATABASE_ERROR]: {
    message: "Database error",
    factory_code: ERROR_CODES.DATABASE_ERROR,
    type: ERROR_TYPES.INTERNAL_ERROR,
  },
} as const;
